import { FC, useEffect, useState } from 'react'
import axios, { AxiosError } from 'axios'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { CrmIntegrationsEndpoint, getCrmIntegrationsUrl } from 'constants/API'
import { EmbedSessionError } from 'components/pages/PurchaseFlow/_main/EmbedSessionError/EmbedSessionError'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'
import { Path } from 'constants/router'
import { embedSession, installEmbedSessionInterceptor } from 'utils/embedSession'
import { forgetEmbedPropertyRef } from 'components/pages/PurchaseFlowRevamp/routing/embedPropertyRef'
import { getBlockedEmbedAncestorOrigin, readEmbedFrameContext } from 'utils/embed/embedParentOrigin'
import { logger } from 'utils/logging'
import { registerMixpanelSuperProperties, unregisterMixpanelSuperProperty } from 'utils/analytics/mixpanel'

export const CODE_PARAM = 'code'
export const ERROR_PARAM = 'error'
/** The onOffice property the iframe was opened from, already copied into the tenant workspace (ISSUE-4351). */
export const PROPERTY_PARAM = 'objectReferenceId'

/** The error codes crm-integrations redirects with; anything else in the URL is logged as `unknown`. */
const CRM_ERROR_CODES = [
  'expired',
  'invalid',
  'not_activated',
  'missing_permission',
  'incomplete_profile',
  'account_refused',
  'verification_required',
  'unavailable',
]

/** Bounds the wait on crm-integrations: a hung call must end on the error screen, not on a spinner. */
const CODE_EXCHANGE_TIMEOUT_MS = 15_000

/** Propstack-only session dimensions, cleared so a previous Propstack session in this document cannot leak them. */
const PROPSTACK_SESSION_DIMENSIONS = ['isOneSystem', 'shopId', 'launch_attempt_id', 'workspace_type']

const LOCALHOST_ORIGIN_PATTERN = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/

/** crm-integrations sends an ISO 639-1 code; anything else is dropped rather than forced on i18n. */
const LANGUAGE_PATTERN = /^[a-z]{2}$/

/** onOffice estate ids are plain ids; anything else is dropped (and logged) rather than looked up. */
const PROPERTY_REF_PATTERN = /^[A-Za-z0-9_-]{1,64}$/

/** What crm-integrations' `/iframe-session` returns for a valid one-time code. */
interface OnOfficeIframeSession {
  bootstrapToken: string
  expiresIn: number
  workspaceId: string
  language: string | null
}

type EntryStep = 'code_exchange' | 'session_exchange'

class EntryStepError extends Error {
  constructor(readonly step: EntryStep, readonly failure: unknown) {
    super(`onOffice entry failed at ${step}`)
  }
}

/**
 * Its own axios instance: the code exchange goes to crm-integrations, not the platform, so it must not carry the
 * platform bearer or go through the embed 401 refresh interceptor of the global axios.
 */
const crmHttp = axios.create({ timeout: CODE_EXCHANGE_TIMEOUT_MS })

/**
 * The entry in flight, shared by every mount: a double effect-invoke (StrictMode) or a remount mid-exchange waits
 * for the same single-use code exchange instead of starting a second one or being left on the spinner.
 */
let entryInFlight: Promise<EntryResult> | null = null

/**
 * The code only opens a session for the onOffice iframe it was issued to. A link to this page outside onOffice
 * (another frame, a top-level tab) would otherwise sign the visitor in to whatever tenant issued the code.
 * Unframed is allowed on localhost only, for local development.
 */
const rejectedFraming = (): string | null => {
  const frame = readEmbedFrameContext('onoffice')
  if (!frame.isFramed) return LOCALHOST_ORIGIN_PATTERN.test(frame.ownOrigin) ? null : 'not_framed'
  return getBlockedEmbedAncestorOrigin(frame) ? 'parent_not_allowed' : null
}

/** The language crm-integrations resolved for the user, or null when it had none. */
const exchange = async (crmIntegrationsUrl: string, code: string): Promise<string | null> => {
  const session = await crmHttp
    .post<OnOfficeIframeSession>(`${crmIntegrationsUrl}${CrmIntegrationsEndpoint.ONOFFICE_IFRAME_SESSION}`, { code })
    .catch((error) => { throw new EntryStepError('code_exchange', error) })
  await embedSession.bootstrap(session.data.bootstrapToken, 'onoffice')
    .catch((error) => { throw new EntryStepError('session_exchange', error) })
  // Registered before the purchase flow mounts: its entry events fire from its own effects.
  registerMixpanelSuperProperties({ workspaceId: embedSession.getWorkspaceId(), crm: 'onoffice' })
  PROPSTACK_SESSION_DIMENSIONS.forEach((name) => unregisterMixpanelSuperProperty(name))
  const language = session.data.language
  return language && LANGUAGE_PATTERN.test(language) ? language : null
}

/** Settled entry: the user's language and the property to preselect when the session opened, `false` when it did not. */
type EntryResult = { language: string | null, propertyRef: string | null } | false

const startEntry = (crmIntegrationsUrl: string, code: string, propertyRef: string | null): Promise<EntryResult> =>
  exchange(crmIntegrationsUrl, code)
    .then((language): EntryResult => ({ language, propertyRef }))
    .catch((error) => {
      // REDACTED scalars only: the error's config carries the code, or the bearer on the platform exchange.
      const step: EntryStep = error instanceof EntryStepError ? error.step : 'code_exchange'
      const failure = (error instanceof EntryStepError ? error.failure : error) as AxiosError | undefined
      const status = failure?.response?.status
      const errorCode = failure?.code
      logger.warn('onOffice iframe session could not be opened', { step, status, errorCode }, {
        context: { component: 'EmbedOnOfficeEntry', action: 'exchangeFailed', metadata: { step, status, errorCode } },
      })
      return false as const
    })
    .finally(() => {
      entryInFlight = null
    })

/**
 * Landing page of the onOffice iframe. crm-integrations redirects here with a one-time `code` (or an `error` code
 * when it could not open the session). The code is exchanged for the platform bootstrap token, the token for the
 * in-memory session pair, and the user continues to the embedded purchase flow, signed in to the tenant workspace.
 */
export const EmbedOnOfficeEntry: FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    installEmbedSessionInterceptor()
    let mounted = true
    const settle = (result: EntryResult) => {
      if (!mounted) return
      if (!result) {
        setFailed(true)
        return
      }
      void queryClient.resetQueries({ queryKey: [EntityKeys.USER_ME, QueryType.GET], exact: true })
      // The purchase flow forces ?lang over the browser detector; it skips the profile language in the embed. It
      // preselects ?objectReferenceId as it does for Propstack, looked up in the session's workspace only.
      const params = new URLSearchParams()
      if (result.language) params.set('lang', result.language)
      // The flow remembers a reference for the tab: an entry without one must not inherit the previous iframe's.
      if (result.propertyRef) params.set(PROPERTY_PARAM, result.propertyRef)
      else forgetEmbedPropertyRef()
      registerMixpanelSuperProperties({ propertyPreselected: !!result.propertyRef })
      const search = params.toString()
      navigate(`${Path.EMBED_PURCHASE_FLOW}${search ? `?${search}` : ''}`, { replace: true })
    }
    const unmount = () => {
      mounted = false
    }

    if (entryInFlight) {
      void entryInFlight.then(settle)
      return unmount
    }

    // The code is single-use, but it must not linger in the address bar, referrers or anything reading location.
    const url = new URL(window.location.href)
    const code = url.searchParams.get(CODE_PARAM)
    const error = url.searchParams.get(ERROR_PARAM)
    const rawPropertyRef = url.searchParams.get(PROPERTY_PARAM)
    const propertyRef = rawPropertyRef && PROPERTY_REF_PATTERN.test(rawPropertyRef) ? rawPropertyRef : null
    if (rawPropertyRef && !propertyRef) {
      // Never the value itself: it is untrusted, hand-editable input.
      logger.warn('onOffice property reference dropped', { length: rawPropertyRef.length }, {
        context: { component: 'EmbedOnOfficeEntry', action: 'propertyRefDropped', metadata: { reason: 'malformed' } },
      })
    }
    url.searchParams.delete(CODE_PARAM)
    url.searchParams.delete(ERROR_PARAM)
    url.searchParams.delete(PROPERTY_PARAM)
    window.history.replaceState(window.history.state, '', url.toString())

    // No new code: an in-SPA return to this page keeps the onOffice session it already opened.
    if (!code && !error && embedSession.getProvider() === 'onoffice') {
      settle({ language: null, propertyRef: null })
      return unmount
    }

    const crmIntegrationsUrl = getCrmIntegrationsUrl()
    let reason: string | null = null
    if (error !== null) reason = CRM_ERROR_CODES.includes(error) ? error : 'unknown'
    else if (!code) reason = 'missing_code'
    else if (!crmIntegrationsUrl) reason = 'crm_integrations_url_missing'
    else reason = rejectedFraming()
    if (reason !== null || !code || !crmIntegrationsUrl) {
      logger.warn('onOffice iframe opened without a session', { reason }, {
        context: { component: 'EmbedOnOfficeEntry', action: 'noSession', metadata: { reason } },
      })
      settle(false)
      return unmount
    }

    // A new code always wins over a live session, which may be another tenant's or another CRM's.
    entryInFlight = startEntry(crmIntegrationsUrl, code, propertyRef)
    void entryInFlight.then(settle)
    return unmount
    // Entry-time only: the code is read once and stripped from the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return failed ? <EmbedSessionError provider="onoffice" /> : <LoadingPage />
}

/** Test-only: clears the shared entry so cases can be exercised in isolation. */
export const _resetEntryLatchForTests = () => {
  entryInFlight = null
}
